import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { auth, db, functions } from "../../services/firebase";
import { ref, get } from "firebase/database";
import { httpsCallable } from "firebase/functions";
import useFetchTestMarks from "../../hooks/useFetchTestMarks";
import "./CheckResultScreen.css";

export default function CheckResultScreen() {
    
    const { domainId, packId, interviewId } = useParams();
    const navigate = useNavigate();
    const { marks, loadingMarks } = useFetchTestMarks(domainId, packId, interviewId);
    const [checking, setChecking] = useState(false);
    const [statusMessage, setStatusMessage] = useState("");

    const userId = auth.currentUser?.uid;

    // ✅ Called when user clicks "View Answers"
    const handleViewAnswers = async () => {
        if (!userId) {
            alert("User not logged in");
            return;
        }

        setChecking(true);
        setStatusMessage("Checking evaluations...");

        try {
            // 1️⃣ Fetch user's answers and evaluations
            const answersSnap = await get(ref(db, `Tests/Answers/${userId}/${domainId}/${packId}/${interviewId}`));
            const evalSnap = await get(ref(db, `Tests/Evaluations/${userId}/${domainId}/${packId}/${interviewId}`));
            const answers = answersSnap.val() || {};
            const evaluations = evalSnap.val() || {};

            const allQIds = Object.keys(answers);
            const missing = allQIds.filter(qid => !evaluations[qid]);

            if (missing.length === 0) {
                console.log("✅ All questions already evaluated.");
                navigate(`/user/viewanswers/${domainId}/${packId}/${interviewId}`);
                return;
            }

            // 2️⃣ Some evaluations missing → call Cloud Function for re-evaluation
            setStatusMessage(`Found ${missing.length} missing evaluations. Re-evaluating...`);

            const reEvalFn = httpsCallable(functions, "reEvaluateAnswer");
            for (const qid of missing) {
                const ans = answers[qid];
                try {
                    await reEvalFn({
                        userId,
                        domainId,
                        packId,
                        interviewId,
                        questionId: qid,
                    });
                    console.log(`✅ Re-evaluated question: ${qid}`);
                } catch (err) {
                    console.error(`❌ Failed to re-evaluate ${qid}:`, err.message);
                }
            }

            setStatusMessage("All evaluations completed. Redirecting...");
            // Small delay to allow writes to complete
            setTimeout(() => {
                navigate(`/user/viewanswers/${domainId}/${packId}/${interviewId}`);
            }, 1500);
        } catch (err) {
            console.error("Error checking evaluations:", err);
            alert("Failed to check evaluations. Try again.");
        } finally {
            setChecking(false);
        }
    };

    if (loadingMarks) {
        return <p className="loading-text">Loading your results...</p>;
    }

    if (!marks) {
        return (
            <div className="result-page">
                <h2>No results found</h2>
                <p>Your test marks are not available yet.</p>
            </div>
        );
    }

    const { totalScore = 0, totalQuestions = 0 } = marks;
    const maxScore = totalQuestions * 10;
    const percentage = totalQuestions > 0 ? ((totalScore / maxScore) * 100).toFixed(2) : 0;

    return (
        <div className="result-page">
            <header className="result-header">
                <h2>🎉 Test Completed!</h2>
                <p>Here’s your performance summary</p>
            </header>

            <div className="result-card">
                <div className="result-row">
                    <span>Total Questions:</span>
                    <strong>{totalQuestions}</strong>
                </div>
                <div className="result-row">
                    <span>Total Score:</span>
                    <strong>{totalScore} / {maxScore}</strong>
                </div>
                <div className="result-row">
                    <span>Percentage:</span>
                    <strong>{percentage}%</strong>
                </div>

                <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${percentage}%` }}></div>
                </div>
            </div>

            <button
                className="view-answers-btn"
                disabled={checking}
                onClick={handleViewAnswers}
            >
                {checking ? "⏳ Checking..." : "📄 View Answers"}
            </button>

            {checking && (
                <p className="checking-status">{statusMessage}</p>
            )}
        </div>
    );
}
